import alerts from '@imt/vue-admin-menus/src/store/alerts';
import toolbox from '@imt/vue-toolbox/src/store';
import utils, {axiosWithAuth, logoutUser,} from '@imt/vue-toolbox/src/utils';
import Vue from 'vue';
import Vuex from 'vuex';

Vue.use(Vuex);
export const axios = axiosWithAuth('/api/v2');

export const getters = {
    searchOptionsFetched: (state) => {
        const hasCombinations = Object.keys(state.combinations || {}).length > 0,
            hasPolicySystems = Object.keys(state.policySystems || {}).length > 0;

        return hasCombinations || hasPolicySystems;
    },
    filterDistinct: (state) => ({
        vendorFilter = '',
        serviceFilter = '',
        sourceFilter = '',
        policySystemFilter = '',
        methodFilter = ''
    } = {}) => {
        // Start with all combinations
        let filteredCombinations = state.combinations || [];

        // Apply filters sequentially to combinations
        if (vendorFilter) {
            filteredCombinations = filteredCombinations.filter(item => item.vendor === vendorFilter);
        }

        if (serviceFilter) {
            filteredCombinations = filteredCombinations.filter(item => item.service === serviceFilter);
        }

        if (sourceFilter) {
            filteredCombinations = filteredCombinations.filter(item => item.source === sourceFilter);
        }

        // Initialize sets for distinct values that are derived from combinations
        const vendorSet = new Set(),
            serviceSet = new Set(),
            sourceSet = new Set();

        // Populate sets from the filtered combinations
        filteredCombinations.forEach(item => {
            vendorSet.add(item.vendor);
            serviceSet.add(item.service);
            // Handle null sources if you want them to appear as an option, or filter them out
            if (item.source !== null) {
                sourceSet.add(item.source);
            }
        });

        // Initialize sets for distinct values that are independent (policySystems, requestMethods)
        const policySystemSet = new Set(),
            methodSet = new Set();

        // Populate policySystemSet
        if (policySystemFilter) {
            // If a specific filter is applied, only add that one
            policySystemSet.add(policySystemFilter);
        } else {
            // Otherwise, add all available policy systems
            state.policySystems?.forEach(ps => {
                policySystemSet.add(ps);
            });
        }

        // Populate methodSet
        if (methodFilter) {
            // If a specific filter is applied, only add that one
            methodSet.add(methodFilter);
        } else {
            // Otherwise, add all available request methods
            state.requestMethods?.forEach(method => {
                methodSet.add(method);
            });
        }

        return {
            vendors: Array.from(vendorSet).sort(),
            services: Array.from(serviceSet).sort(),
            sources: Array.from(sourceSet).sort(),
            policySystems: Array.from(policySystemSet).sort(),
            requestMethods: Array.from(methodSet).sort(),
        };
    },
    ratingFields() {
        return [
            {key: 'id', label: 'ID', sortable: true},
            {key: 'createdAt', label: 'Created At', sortable: true},
            {key: 'policySystem', label: 'Policy System', sortable: true},
        ];
    },
    recordTypeOptions() {
        return [
            {text: 'Service', value: 'service'},
            {text: 'Rating', value: 'rating'}
        ];
    },
    serviceFields() {
        return [
            {key: 'company', label: 'Company', sortable: true},
            {key: 'vendor', label: 'Vendor', sortable: true},
            {key: 'source', label: 'Source', sortable: true},
            {key: 'service', label: 'Service', sortable: true},
            {key: 'date', label: 'Date', sortable: true},
        ];
    },
};

export const actions = {
    async fetchDistinct(context, {startDate, endDate} = {}) {
        const response = await axios.get('/distinct/', {
            params: {
                ...(startDate && { start_date: `${startDate}T00:00:00` }),
                ...(endDate && { end_date: `${endDate}T23:59:59` })
            }
        });

        context.commit('SET_DATA', {type: 'combinations', data: response.data?.combinations});
        context.commit('SET_DATA', {type: 'policySystems', data: response.data?.policySystems});

        return response.data;
    },
    async fetchRecordById(context, {id, isService, fromSearch}) {
        const response = await axios.get(`/${isService ? 'services' : 'ratings'}/${id}/`);

        if (fromSearch && response.data) {
            context.commit('SET_DATA', {type: 'results', data: [utils.dataFormatter.deserialize(response.data)]});
            context.commit('SET_DATA', {type: 'count', data: 1});
        } else if (!fromSearch && response.data) {
            context.commit('SET_DATA', {
                type: 'selectedResult',
                data: utils.dataFormatter.deserialize(response.data)
            });
        }
    },
    async fetchSearchResults(context, baseURL) {
        let response = await axios.get(baseURL),
            results = utils.dataFormatter.deserialize(response.data);

        context.commit('SET_DATA', {type: 'results', data: results});
        context.commit('SET_DATA', {type: 'count', data: response.data.meta.pagination.count || 0});
    },
    logout(context) {
        logoutUser();
        context.commit('SET_AUTH_TOKEN', '');
        window.location = `${process.env.VUE_APP_ADMIN_URL_USERS}`;
    },
    resetPassword() {
        window.location = `${process.env.VUE_APP_ADMIN_URL_USERS}/change-password`;
    },
};

export const mutations = {
    SET_AUTH_TOKEN(state, token) {
        state.authToken = token;
        axios.defaults.headers.Authorization = token;
    },
    SET_DATA(state, {type, data}) {
        state[type] = data;
    },
    CLEAR_SEARCH_FIELDS(state) {
        state.searchFields = {};
    },
    SET_SEARCH_FIELDS(state, searchField) {
        if ((typeof searchField.value !== 'boolean' && !searchField.value) || (Array.isArray(searchField.value) && !searchField.value.length)) {
            Vue.delete(state.searchFields, searchField.key);
        } else {
            Vue.set(state.searchFields, searchField.key, searchField.value);
        }
    }
};

export const state = () => {
    return {
        count: 0,
        initialSearchSubmitted: false,
        isValidSearch: true,
        loading: false,
        results: [],
        searchFields: {},
        selectedResult: {},
        submitted: false,
        combinations: {},
        policySystems: {},
        requestMethods: [
            'DELETE',
            'GET',
            'PATCH',
            'POST',
            'PUT',
        ],
    };
};

export const storeConfig = {
    modules: {
        alerts,
        toolbox,
    },
    actions,
    getters,
    mutations,
    state: state(),
};

export default new Vuex.Store(storeConfig);
